import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { biApi } from '../api/bi'
import Boton from '../components/Boton'
import CampoFecha from '../components/CampoFecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtValor, tonoDe } from '../components/reportes/formatoReporte'
import BarraPeriodo, { Chips } from '../components/reportes/BarraPeriodo'

/**
 * BI → Reportes (Martin, 15 oct 2026): reportes fijos que se consultan cuando
 * quieras, con las fechas variables resueltas al abrir (año actual, último día
 * con ventas…). La página no conoce ningún reporte: dibuja el documento que
 * devuelve /api/bi/reportes/{clave} (KPIs, avisos, tabla, cómo está calculado).
 * Un reporte nuevo = una clase en el backend; acá no se toca nada.
 */
export default function BiReportesPage() {
  const { clave } = useParams()
  return clave ? <Reporte clave={clave} /> : <Catalogo />
}

function Catalogo() {
  const navigate = useNavigate()
  const [lista, setLista] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => { biApi.reportes().then(({ data }) => setLista(data)).catch(() => setError('No se pudo leer la lista de reportes.')) }, [])
  return (
    <div>
      <h2 style={st.title}>Reportes</h2>
      <p style={st.sub}>Reportes fijos: se actualizan solos con las ventas de cada día. También están en la versión móvil.</p>
      {error && <div style={st.error}>{error}</div>}
      {!lista && !error && <p style={st.sub}>Cargando…</p>}
      {lista && lista.length === 0 && <p style={st.sub}>Todavía no hay reportes.</p>}
      <div style={st.grilla}>
        {(lista || []).map(r => (
          <button key={r.clave} type="button" className="rep-card" style={st.tarjeta} onClick={() => navigate(`/bi/reportes/${r.clave}`)}>
            <span style={st.tarjetaTitulo}>{r.titulo}</span>
            <span style={st.tarjetaDesc}>{r.descripcion}</span>
          </button>
        ))}
      </div>
      <style>{'.rep-card:hover{border-color:' + colors.linkBorder + ' !important}'}</style>
    </div>
  )
}

function Reporte({ clave }) {
  const navigate = useNavigate()
  const [params, setParams] = useState({})
  const [r, setR] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)

  const cargar = useCallback(async (p) => {
    setCargando(true); setError(null)
    try {
      const { data } = await biApi.reporte(clave, p)
      setR(data)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo armar el reporte.')
    } finally { setCargando(false) }
  }, [clave])
  // Carga inicial: el estado se toca solo al llegar la respuesta.
  useEffect(() => {
    let vivo = true
    biApi.reporte(clave, {})
      .then(({ data }) => { if (vivo) { setR(data); setError(null) } })
      .catch((e) => { if (vivo) setError(e.response?.data?.message || 'No se pudo armar el reporte.') })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
  }, [clave])

  // Un cambio de filtro = lo elegido + lo que cambia; lo vacío no viaja.
  const aplicar = (patch) => {
    const p = Object.fromEntries(Object.entries({ ...params, ...patch }).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)))
    setParams(p); cargar(p)
  }
  const cambiar = (k, v) => aplicar({ [k]: v || undefined })
  const simples = r ? r.parametros.filter(p => p.tipo === 'anio' || p.tipo === 'fecha') : []
  const filtros = r ? r.parametros.filter(p => !['anio', 'fecha'].includes(p.tipo)) : []

  return (
    <div>
      <div style={st.header}>
        <div>
          <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
          <h2 style={{ ...st.title, marginTop: space.md }}>{r?.titulo || 'Reporte'}</h2>
          {r?.descripcion && <p style={st.sub}>{r.descripcion}</p>}
        </div>
        {r && (
          <div style={st.params}>
            {simples.map(p => (
              <label key={p.clave} style={st.param}>
                <span>{p.label}</span>
                {p.tipo === 'anio' ? (
                  <select style={st.select} value={p.valor} onChange={e => cambiar(p.clave, e.target.value)}>
                    {p.opciones.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <CampoFecha value={p.valor} onChange={v => cambiar(p.clave, v)} style={{ width: 120 }} />
                )}
                {p.nota && <span style={st.paramNota}>{p.nota}</span>}
              </label>
            ))}
            {simples.length > 0 && Object.keys(params).length > 0 && <Boton variante="ghost" sm onClick={() => { setParams({}); cargar({}) }}>Volver a hoy</Boton>}
          </div>
        )}
      </div>

      {filtros.length > 0 && (
        <div style={st.filtros}>
          {filtros.filter(p => p.tipo === 'periodo').map(p => (
            <div key={p.clave} style={{ minWidth: 0, maxWidth: 460, flex: '1 1 360px' }}><BarraPeriodo p={p} onCambiar={aplicar} /></div>
          ))}
          <div style={{ display: 'grid', gap: space.sm, flex: '1 1 320px', minWidth: 0 }}>
            {filtros.filter(p => p.tipo === 'opcion' || p.tipo === 'multi').map(p => <Chips key={p.clave} p={p} onCambiar={aplicar} />)}
          </div>
        </div>
      )}

      {error && <div style={st.error}>{error}</div>}
      {cargando && !r && <p style={st.sub}>Armando el reporte…</p>}
      {r && (
        <div style={{ opacity: cargando ? 0.5 : 1, transition: 'opacity .15s' }}>
          {r.avisos.map((a, i) => <div key={i} style={st.aviso}>{a}</div>)}

          <div style={st.kpis}>
            {r.kpis.map(k => (
              <div key={k.label} style={st.kpi}>
                <div style={{ ...st.kpiValor, color: k.tono === 'neg' ? colors.err : k.tono === 'pos' ? colors.ok : colors.text }}>{fmtValor(k.valor, k.formato)}</div>
                <div style={st.kpiLabel}>{k.label}</div>
                {k.nota && <div style={st.kpiNota}>{k.nota}</div>}
              </div>
            ))}
          </div>

          <Tabla tabla={r.tabla} />

          {r.secciones.map(s => (
            <div key={s.titulo} style={st.seccion}>
              <h3 style={st.seccionTitulo}>{s.titulo}</h3>
              <dl style={st.def}>
                {s.items.map(it => [<dt key={it.t + 't'} style={st.dt}>{it.t}</dt>, <dd key={it.t + 'd'} style={st.dd}>{it.d}</dd>])}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Tabla({ tabla }) {
  const { columnas, filas, total } = tabla
  const alinear = c => (NUM.includes(c.formato) ? 'right' : c.formato === 'dia' ? 'center' : 'left')
  const celda = (fila, c) => {
    const v = fila[c.clave]
    if (c.formato === 'dia') return <span style={{ color: colors.textMeta, fontSize: text.sec }}>{v}</span>
    if (v == null && (NUM.includes(c.formato))) return <span style={{ color: colors.textPlh }}>—</span>
    const tono = tonoDe(v, c.formato)
    return <span style={{ color: tono === 'neg' ? colors.err : tono === 'pos' ? colors.ok : undefined }}>{fmtValor(v, c.formato)}</span>
  }
  return (
    <div style={st.tablaWrap}>
      <style>{'.rep-tr:hover td{background:' + colors.bgSoft + '}'}</style>
      <table style={{ ...st.tabla, minWidth: columnas.length > 5 ? 980 : 0 }}>
        <thead>
          <tr>{columnas.map(c => <th key={c.clave} style={{ ...st.th, textAlign: alinear(c) }}>{c.formato === 'dia' ? '' : c.label}</th>)}</tr>
        </thead>
        <tbody>
          {filas.map((f, i) => f.separador
            ? <tr key={'s' + i}><td colSpan={columnas.length} style={st.separador}>{f.separador}</td></tr>
            : (
              <tr key={i} className="rep-tr" style={st.tr}>
                {columnas.map((c, ci) => <td key={c.clave} style={{ ...st.td, textAlign: alinear(c), ...(f.sub && ci === 0 ? { paddingLeft: space['3xl'], color: colors.textSec } : {}), ...(c.formato !== 'texto' && c.formato !== 'dia' ? sx.mono : c.clave === 'fecha' ? sx.mono : {}), ...(c.formato === 'texto' && c.movil === 'secundaria' ? { whiteSpace: 'normal', color: colors.textSec, fontSize: text.sec } : {}) }}>{celda(f, c)}</td>)}
              </tr>
            ))}
          {total && (
            <tr>
              {columnas.map(c => <td key={c.clave} style={{ ...st.td, ...st.total, textAlign: alinear(c), ...(NUM.includes(c.formato) ? sx.mono : {}) }}>{c.clave in total ? celda(total, c) : ''}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const NUM = ['pesos', 'pct', 'entero']

const st = {
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: space.lg, flexWrap: 'wrap', marginBottom: space.xl },
  params: { display: 'flex', alignItems: 'flex-end', gap: space.lg, flexWrap: 'wrap' },
  param: { display: 'grid', gap: 4, fontSize: text.sec, color: colors.textSec },
  paramNota: { fontSize: text.meta, color: colors.textMeta },
  select: { border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, padding: '5px 8px', fontSize: text.body, background: colors.bg, color: colors.text, fontFamily: font.sans },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: '8px 12px', marginBottom: space.lg, fontSize: text.sec },
  aviso: { background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.sm, padding: '8px 12px', marginBottom: space.md, fontSize: text.sec },
  grilla: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: space.lg, marginTop: space.xl },
  tarjeta: { ...sx.card, display: 'grid', gap: space.xs, textAlign: 'left', cursor: 'pointer', fontFamily: font.sans },
  tarjetaTitulo: { fontSize: text.body, fontWeight: 600, color: colors.text },
  tarjetaDesc: { fontSize: text.sec, color: colors.textSec },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: space.md, margin: `${space.md}px 0 ${space.xl}px` },
  kpi: { ...sx.card, padding: `${space.lg}px ${space.xl}px` },
  kpiValor: { ...sx.mono, fontSize: 18, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  kpiLabel: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  kpiNota: { fontSize: text.meta, color: colors.textMeta },
  tablaWrap: { ...sx.card, padding: 0, overflow: 'auto', maxHeight: '70vh' },
  tabla: { ...sx.table, minWidth: 980 },
  th: { ...sx.th, position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' },
  tr: { height: 40 },
  td: { ...sx.td, padding: `0 ${space.lg}px`, whiteSpace: 'nowrap' },
  separador: { background: colors.bgSoft, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, padding: `6px ${space.lg}px` },
  total: { background: colors.bgSoft, fontWeight: 600, height: 40, position: 'sticky', bottom: 0, borderTop: `1px solid ${colors.border}` },
  seccion: { marginTop: space['3xl'], maxWidth: 860 },
  filtros: { ...sx.card, display: 'flex', flexWrap: 'wrap', gap: space['2xl'], alignItems: 'flex-start', padding: `${space.lg}px ${space.xl}px`, marginBottom: space.lg },
  seccionTitulo: { margin: `0 0 ${space.md}px`, fontSize: text.body, fontWeight: 600, color: colors.text },
  def: { display: 'grid', gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)', gap: `${space.sm}px ${space.xl}px`, margin: 0 },
  dt: { fontSize: text.sec, fontWeight: 600, color: colors.text },
  dd: { margin: 0, fontSize: text.sec, color: colors.textSec },
}
